import React, { useState } from "react";
import PodImage from "@/components/PodImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, CheckCircle2, Clock, Truck } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

// Delivery status chip for a paint order (Ordered → On run → Delivered) with a POD viewer.
export default function PaintDeliveryStatus({ order, compact }) {
  const [open, setOpen] = useState(false);
  const pod = order.pod;
  if (order.delivered_at) {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} data-testid={`paint-status-${order.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100">
          <CheckCircle2 className="h-3 w-3" /> Delivered {!compact && fmt(order.delivered_at)}
          {pod?.photos?.length > 0 && <Camera className="ml-0.5 h-3 w-3" />}
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg" data-testid="paint-pod-viewer">
            <DialogHeader>
              <DialogTitle>Proof of delivery — {order.customer?.name}</DialogTitle>
              <DialogDescription>
                Delivered {fmt(order.delivered_at)}{order.delivered_by_name ? ` by ${order.delivered_by_name}` : ""}
                {pod?.received_by ? ` · received by ${pod.received_by}` : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <p className="text-xs text-muted-foreground">{order.customer?.address}</p>
              <p className="text-xs">{(order.lines || []).map((l) => `${l.qty}× ${l.label}`).join(", ")}</p>
              {pod?.note && <p className="rounded bg-secondary px-2 py-1 text-xs">Driver note: {pod.note}</p>}
              {pod?.customer_sms_at && <p className="text-[11px] text-emerald-700">Delivery text sent to {order.customer?.phone}</p>}
              {pod?.photos?.length ? (
                <div className="grid grid-cols-2 gap-2">
                  {pod.photos.map((p, i) => <PodImage key={p.path} url={p.url} testid={`paint-pod-photo-${i}`} className="max-h-56 w-full rounded-md border object-cover" />)}
                </div>
              ) : <p className="text-xs text-muted-foreground">No photo was taken for this delivery.</p>}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  if (order.driver_name) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700" data-testid={`paint-status-${order.id}`}>
        <Truck className="h-3 w-3" /> On run{!compact && ` · ${order.driver_name.split(" ")[0]}`}{!compact && order.run_date ? ` · ${order.run_date.slice(5)}` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`paint-status-${order.id}`}>
      <Clock className="h-3 w-3" /> Ordered
    </span>
  );
}
